<template>
  <div
    v-if="records.length"
    class="s-hero-slider"
    :class="heroSliderArticleClass">
    <div
      v-if="currentSlide.vid && desktop"
      class="s-hero-slider__player-container">
      <YouTubeVideo
        ref="heroYouTubePlayer"
        :key="currentSlide.id"
        v-model="soundState"
        :youtube-id="currentSlide.vid"
        :autoplay="1"
        :muted="!soundState"
        :video-placeholder="currentSlide.thumb"
        @video-percent="assignVideoPercent"
        @on-player-state-change="youtubeVideoStateChanged" />
    </div>
    <div
      v-else-if="
        !movieCurrentSlide || !desktop || (!currentSlide.vid && desktop)
      "
      class="s-hero-slider__image-cover"
      :class="{ 's-hero-slider__image-cover--mobile': mobile || tablet }">
      <img :src="currentSlide.thumb" :alt="currentSlide.title" />
    </div>
    <transition name="fade">
      <div v-if="showText" class="s-hero-slider__container">
        <div v-if="movieCurrentSlide" class="s-hero-slider__labels">
          <div
            v-if="!currentSlide.isNow"
            class="e-label e-label--bg e-label--playing">
            <span>{{ dictionary.ticketsFrom }}</span>
            {{ showOpenDate(currentSlide.due) }}
          </div>
          <div
            v-else-if="isDueAfterNow(currentSlide.due)"
            class="e-label e-label--bg e-label--playing">
            <span>{{ dictionary.premier }}</span>
            {{ showOpenDate(currentSlide.due) }}
          </div>
          <div v-else class="e-label e-label--bg e-label--playing">
            <span>{{ dictionary.playingNow }}</span>
          </div>

          <div
            v-if="currentSlide.transl"
            class="e-label e-label--bg e-label--translated">
            {{ dictionary.translated }}
          </div>
        </div>

        <div class="s-hero-slider__wrapper">
          <div class="s-hero-slider__title-elements">
            <!-- <transition-group name="fade" mode="out-in">       -->
            <div class="s-hero-slider__title" v-html="currentSlide.title"></div>
            <div
              class="s-hero-slider__subtitle"
              v-html="currentSlide.subtitle"></div>
            <!-- </transition-group> -->
          </div>

          <div
            v-if="desktop && movieCurrentSlide"
            class="s-hero-slider__description"
            v-html="currentSlide.desc"></div>

          <div v-if="movieCurrentSlide" class="s-hero-slider__movie-elements">
            <div v-if="currentSlide.pg" class="e-k">{{ currentSlide.pg }}</div>
            <div v-if="currentSlide.genre" class="movie-attr--movie">
              {{ currentSlide.genre }}
            </div>
            <div v-if="currentSlide.dur" class="movie-attr--time">
              <span>{{ currentSlide.dur }}</span>
            </div>
          </div>
        </div>

        <div
          v-if="
            (movieCurrentSlide && currentSlide.isNow) ||
            (currentSlide.cta && currentSlide.cta.url)
          "
          class="s-hero-slider__btn-area">
          <div
            v-if="movieCurrentSlide && currentSlide.isNow"
            class="s-hero-slider__tickets-btn"
            @click="
              redirectIntoBooking(configuration.baseUrl, currentSlide.movieId)
            ">
            <span>{{ dictionary.bookTickets }}</span>
          </div>
          <div class="s-hero-slider__more-cta">
            <a
              v-if="currentSlide.cta && currentSlide.cta.url"
              class="s-hero-slider__more-btn"
              :target="currentSlide.cta ? currentSlide.cta.target : null"
              :class="{
                's-hero-slider__more-btn--primary': !movieCurrentSlide,
              }"
              :href="currentSlide.cta ? currentSlide.cta.url : null">
              <span>{{ currentSlide.cta.title }}</span>
            </a>
            <div
              v-if="currentSlide.trailerId"
              class="s-hero-slider__trailer-btn"
              @click="openTrailerModal(currentSlide.trailerId)">
              Trailer
            </div>
          </div>
        </div>

        <div class="s-hero-slider__elements">
          <div v-if="movieSlides.length > 1" class="s-hero-slider__thumbs">
            <div
              v-for="(movie, index) in getSlides"
              :key="movie.id"
              class="s-hero-slider__thumbs__img"
              :class="{
                's-hero-slider__thumbs__img--active': isActiveSlide(movie.id),
                's-hero-slider__thumbs__img--not-active': !isActiveSlide(
                  movie.id,
                ),
              }"
              @click="chooseSlide(index)">
              <img :src="movie.thumb" :alt="currentSlide.title" />
              <div
                v-if="!isCurrentActiveSlide(movie.id)"
                class="s-hero-slider__thumbs-title"
                v-html="movie.title"></div>
              <YouTubeVideoThumb
                :key="currentSlide.id"
                :progress="thumbnailTimer"
                :active="isActiveSlide(movie.id)"
                class="s-hero-slider__thumb-container" />
            </div>
          </div>
          <div
            v-if="desktop && currentSlide.vid"
            class="s-hero-slider__movie-btns">
            <div :class="playingControlBtn" @click="playPauseTrailer"></div>
            <div :class="soundControlBtn" @click="changeTrailerSound"></div>
          </div>
        </div>
      </div>
    </transition>
    <ModalOverlay
      v-model="showTrailerModal"
      video-modal
      :swiper-data="[youtubeVideoId]"
      background-class="b-pop-up--video b-pop-up__holder"
      header-background-class="b-pop-up__header"
      @close="trailerModalClosed">
      <template #header>
        <div class="b-pop-up__header__title">Trailer</div>
      </template>
    </ModalOverlay>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import YouTubeVideo from '../components/YouTubeVideo.vue'
import YouTubeVideoThumb from '../components/YouTubeVideoThumb.vue'
import { getData } from '../../lib/lib'
import {
  getFormattedDateWithSeparators,
  normalizeToAthens,
} from '../../lib/date'
const {
  slides: records,
  dictionary,
  configuration,
} = getData('heroSliderStripData')
import bookingRedirect from '../../lib/bookingRedirect'
import ModalOverlay from '../components/ModalOverlay.vue'
import useViewport from '../../features/viewport.js'
import { useSlider } from '../../features/slider'
import jQuery from 'jquery'

const {
  startSlider,
  thumbnailTimer,
  assignVideoPercent,
  setSlides,
  activeSlide,
  isActiveSlide,
  getSlides,
  duration,
  chooseSlide,
  playPauseTrailer,
  pauseTrailer,
  playTrailer,
  playingControlBtn,
  youtubeVideoStateChanged,
  heroYouTubePlayer,
  movieCurrentSlide,
  muteTrailer,
  unMuteTrailer,
} = useSlider()

const { desktop, tablet, mobile } = useViewport()

const { redirectIntoBooking } = bookingRedirect()

// props

// emits

// reactive data

const soundState = ref(false)

const movieSlides = ref([])
const currentSlideIndex = ref(0)
const showTrailerModal = ref(false)
const youtubeVideoId = ref('')

const silderDuration = ref(configuration.slideDuration)

// hooks
onMounted(() => {
  movieSlides.value = records

  if (records.length) {
    jQuery('body').addClass('has-hero')

    activeSlide.value = records[currentSlideIndex.value]

    setSlides(records, 'thumb')
    duration.value
    startSlider(silderDuration.value)
  }

  setHeightBasedOnSlideTypes()
})

// methods

const currentSlide = computed(() => {
  return activeSlide.value
})

const soundControlBtn = computed(() => {
  const prefix = 's-hero-slider__sound-btn-'
  return soundState.value ? `${prefix}off` : `${prefix}on`
})

const heroSliderArticleClass = computed(() => {
  return !currentSlide.value.vid && movieSlides.value.length === 1
    ? 's-hero-slider--single-article'
    : ''
})

const isDueAfterNow = (due) => {
  return due ? normalizeToAthens(due) > new Date() : false
}

const isCurrentActiveSlide = (movieId) => {
  return activeSlide.value.id === movieId
}

const showOpenDate = (premierDate) => {
  return getFormattedDateWithSeparators(
    premierDate,
    'en-GB',
    { year: 'numeric', month: '2-digit', day: '2-digit' },
    '.',
  )
}

const changeTrailerSound = () => {
  if (soundState.value) {
    muteTrailer()
    soundState.value = false
  } else {
    unMuteTrailer()
    soundState.value = true
  }
}
const openTrailerModal = (videoId) => {
  youtubeVideoId.value = videoId
  showTrailerModal.value = true
  soundState.value = false
  heroYouTubePlayer.value.mute()
  pauseTrailer()
}

const trailerModalClosed = () => {
  playTrailer()
}

const setHeightBasedOnSlideTypes = () => {
  if (!records.length) {
    jQuery('.s-hero').addClass('s-hero--no-data')
    return false
  }

  const hasMovieSlide = records.some((record) => record.movieSlide === true)

  if (!hasMovieSlide) {
    jQuery('.s-hero').addClass('s-hero--article')
  }
}

let timer = 0
const showText = ref(false)
watch(currentSlide, async () => {
  showText.value = false

  clearTimeout(timer)
  timer = await new Promise((resolve) => setTimeout(resolve, 10))
  showText.value = true
})
</script>
